import { useAmplitude } from '@crehana/analytics';
import ArrowRight from '@crehana/icons/outline/ArrowRight';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { Clamp, colors, LazyImageResponsive } from '@crehana/ui';
import { useUITheme } from '@crehana/web';
import { clsx } from 'clsx';
import { useRouter } from 'next/router';

import { ACSENDO_DOMAIN } from '@/shared/constants';

import { regexSubDomain } from './../../utils';
import { CardWrapper } from './Card.styles';
import type { CardProps as CP } from './types';

interface CardProps extends CP {
  cta: string;
  description: string;
  label: string;
  title: string;
  subdomain: string;
}

const { white, baseDark } = colors;

const Card: React.FC<React.PropsWithChildren<CardProps>> = ({
  cta,
  description,
  hover,
  imageWhite,
  imageDark,
  inverse,
  label,
  title,
  url,
  isExternal,
  subdomain,
}) => {
  const { logEvent } = useAmplitude();
  const { isDark } = useUITheme();
  const router = useRouter();
  const { slug } = router.query as { slug: string };

  const image = isDark ? imageDark : imageWhite;
  const imageUrl = `https://crehana-vercel-images-prd.imgix.net/public/${image}?auto=format&fit=clip`;

  const acsendoUrl = `${ACSENDO_DOMAIN}${url}`;
  const newAcsendoUrl = acsendoUrl.replace(regexSubDomain, `$1${subdomain}.$3`);

  const handleClick = () => {
    const redirectUrl = isExternal
      ? newAcsendoUrl
      : url.replace('[slug]', slug);

    let didNavigate = false;
    const navigate = () => {
      if (didNavigate) {
        return;
      }
      didNavigate = true;

      if (isExternal) {
        window.open(redirectUrl, '_self');
        return;
      }

      router.push(redirectUrl);
    };

    logEvent({
      event: 'Click Home Item Admin',
      data: {
        'Click URL': redirectUrl,
        'Item name': title,
        Organization: slug,
      },
    });

    navigate();
  };

  return (
    <CardWrapper
      isDark={isDark}
      inverse={inverse}
      hover={hover}
      onClick={handleClick}
      className={clsx(
        'flex rounded-10 overflow-hidden',
        inverse && 'flex-row-reverse',
      )}
    >
      <div className="flex flex-1 flex-col p-24">
        <p className="tw-font-label1">{label}</p>
        <hr
          className={clsx(
            'w-20 h-1 mt-12 mb-20',
            isDark ? 'bg-white' : 'bg-black',
          )}
        />
        <p className="tw-font-subtitle2 mb-8">{title}</p>
        <Clamp clamp={2} className="tw-font-body5 mb-12 h-40 subtitle">
          {description}
        </Clamp>
        {/* TODO: talk with the code owner to fix this code so we can enable this rule again */}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a
          className={clsx(
            'tw-font-link1 flex items-center cursor-pointer hover:underline',
            isDark ? 'text-white' : 'text-base-dark',
          )}
          target="_blank"
          rel="noreferrer"
        >
          {cta}
          <ArrowRight
            className="ml-8"
            color={isDark ? white : baseDark}
            size={24}
          />
        </a>
      </div>
      <div className="flex h-full w-200 min-w-200 max-w-200">
        <LazyImageResponsive
          sources={{
            default: imageUrl,
            sm: imageUrl,
            md: imageUrl,
            lg: imageUrl,
          }}
          placeholder={title}
          alt={title}
          className="w-full"
          imgClassName="object-cover w-auto"
        />
      </div>
    </CardWrapper>
  );
};

export default Card;
